import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Collapse, Descriptions, Empty, Form, Input, Modal, Row,
  Rate, Select, Space, Spin, Switch, Table, Tag, Typography, message,
} from 'antd';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  LockOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  StarFilled,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import * as AntdIcons from '@ant-design/icons';
import * as api from '../api/client';
import type { AgentRecord, AgentExecution, AgentScheduleRecord, AgentVersion, ToolRecord } from '../api/client';
import RichPostEditor from './RichPostEditor';
import type { ProjectFolderSummary } from '../types';

const { Text, Title, Paragraph } = Typography;

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const TIME_ZONE_OPTIONS = [
  { label: 'Local timezone', value: LOCAL_TIMEZONE },
  { label: 'Eastern - New York', value: 'America/New_York' },
  { label: 'Central - Chicago', value: 'America/Chicago' },
  { label: 'Mountain - Denver', value: 'America/Denver' },
  { label: 'Mountain - Phoenix', value: 'America/Phoenix' },
  { label: 'Pacific - Los Angeles', value: 'America/Los_Angeles' },
  { label: 'Alaska - Anchorage', value: 'America/Anchorage' },
  { label: 'Hawaii - Honolulu', value: 'Pacific/Honolulu' },
  { label: 'UTC', value: 'UTC' },
].filter((option, index, all) => all.findIndex((item) => item.value === option.value) === index);

const STATUS_COLOR: Record<string, string> = {
  RUNNING: 'processing',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
  CREATED: 'default',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  RUNNING: <ClockCircleOutlined />,
  COMPLETED: <CheckCircleOutlined />,
  FAILED: <CloseCircleOutlined />,
  CANCELLED: <CloseCircleOutlined />,
};

const renderAgentIcon = (icon: string, size = 40): React.ReactNode => {
  if (!icon) return <RocketOutlined style={{ fontSize: size, color: '#10a37f' }} />;
  if (icon.startsWith('data:') || icon.startsWith('http://') || icon.startsWith('https://')) {
    return (
      <img
        src={icon}
        alt="agent icon"
        style={{ width: size, height: size, borderRadius: 8, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  const IconComp = (AntdIcons as any)[icon] as React.ComponentType<any> | undefined;
  if (IconComp) {
    return <IconComp style={{ fontSize: size, color: '#10a37f' }} />;
  }
  return <RocketOutlined style={{ fontSize: size, color: '#10a37f' }} />;
};

const normalizeStatus = (status: string): string => (status || 'CREATED').toUpperCase();

const parseScheduleInputs = (raw: string): Record<string, any> => {
  try {
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const schemaValueExample = (fieldDef: any): string => {
  const type = typeof fieldDef === 'object' && fieldDef?.type ? String(fieldDef.type).toLowerCase() : 'string';
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'password') return 'string';
  return 'string';
};

const buildApiSampleBody = (schema: Record<string, any>): Record<string, any> => ({
  inputs: Object.fromEntries(
    Object.entries(schema || {}).map(([key, value]) => [key, schemaValueExample(value)]),
  ),
});

const valueText = (children: React.ReactNode) => (
  <Text style={{ color: '#e2e8f0' }}>{children}</Text>
);

interface Props {
  agentId: string;
  userId: string;
  projectFolders?: ProjectFolderSummary[];
  onPrepareProjectFolders?: () => Promise<void>;
  onBack: () => void;
  onEdit: (agent: AgentRecord) => void;
  onOpenTask?: (taskId: string) => void;
}

const normalizeToolId = (tool: any): string => {
  if (typeof tool === 'string') return tool.trim();
  if (!tool || typeof tool !== 'object') return '';
  return String(tool.name || tool.id || tool.tool_name || '').trim();
};

const normalizeToolRecord = (tool: any): ToolRecord | null => {
  if (typeof tool === 'string') {
    const name = tool.trim();
    return name ? { name, description: '' } : null;
  }
  if (!tool || typeof tool !== 'object') return null;
  const name = normalizeToolId(tool);
  if (!name) return null;
  return {
    ...tool,
    name,
    description: String(tool.description || tool.summary || ''),
  };
};

const getToolInputSchema = (tool: ToolRecord | undefined): Record<string, any> => {
  if (!tool || typeof tool !== 'object') return {};
  const inputSchema = (tool as any).input_schema;
  if (inputSchema && typeof inputSchema === 'object' && !Array.isArray(inputSchema)) return inputSchema;
  const inputs = (tool as any).inputs;
  if (inputs && typeof inputs === 'object' && !Array.isArray(inputs)) return inputs;
  return {};
};

const getToolInputEntries = (tool: ToolRecord | undefined): [string, any][] => {
  const schema = getToolInputSchema(tool);
  const properties = schema.properties && typeof schema.properties === 'object' && !Array.isArray(schema.properties)
    ? schema.properties
    : schema;
  const requiredNames = new Set(Array.isArray(schema.required) ? schema.required.map(String) : []);
  return Object.entries(properties)
    .filter(([name]) => !['properties', 'required', '$schema', 'type', 'title', 'description'].includes(name))
    .map(([name, def]) => {
      const fieldDef: Record<string, any> = def && typeof def === 'object' && !Array.isArray(def) ? { ...def } : {};
      return [
        name,
        {
          ...fieldDef,
          required: fieldDef.required !== undefined ? Boolean(fieldDef.required) : requiredNames.has(name),
          secret: Boolean(
            fieldDef.secret ||
            fieldDef.format === 'password' ||
            /(pass|password|token|secret|api[_-]?key|credential)/i.test(name),
          ),
        },
      ];
    });
};

const buildToolInputSchema = (
  tools: ToolRecord[],
  existingSchema: Record<string, any>,
  hasProfileConnectors = false,
): Record<string, any> => {
  const existingKeys = new Set(Object.keys(existingSchema || {}).map((key) => key.toLowerCase()));
  const schema: Record<string, any> = {};
  tools.forEach((tool) => {
    getToolInputEntries(tool).forEach(([name, def]) => {
      const normalized = name.trim().toLowerCase();
      if (hasProfileConnectors && (normalized === 'connector_instance_id' || /(pass|password|token|secret|api[_-]?key|credential)/i.test(name))) return;
      if (!normalized || existingKeys.has(normalized) || schema[name]) return;
      schema[name] = {
        ...def,
        required: false,
        description: def.description || `${normalizeToolId(tool)} tool input`,
      };
    });
  });
  return schema;
};

const mergeNonEmptyValues = (...sources: Record<string, string>[]): Record<string, string> => {
  const merged: Record<string, string> = {};
  sources.forEach((source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (String(value ?? '').trim()) merged[key] = value;
    });
  });
  return merged;
};

const optionalSocialContentSchema = (schema: Record<string, any>, social: boolean): Record<string, any> => {
  if (!social) return schema;
  return Object.fromEntries(Object.entries(schema).map(([name, definition]) => [
    name,
    /^(post[_\s-]*(content|context|text)|source[_\s-]*content|content|context|text|body|caption)$/i.test(name)
      ? { ...(definition && typeof definition === 'object' ? definition : {}), required: false }
      : definition,
  ]));
};

const validateRequiredInputs = (schema: Record<string, any>, values: Record<string, string>, label: string): boolean => {
  const missing = Object.entries(schema || [])
    .filter(([, def]) => {
      const fieldDef = typeof def === 'object' && def ? def : {};
      return fieldDef.required !== false;
    })
    .map(([key]) => key)
    .filter((key) => !String(values[key] ?? '').trim());
  if (missing.length > 0) {
    message.warning(`${label}: ${missing.join(', ')} required`);
    return false;
  }
  return true;
};

/** Render a dynamic execute form from an input_schema record */
const DynamicInputForm: React.FC<{
  schema: Record<string, any>;
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  emptyText?: string;
  richPost?: boolean;
}> = ({ schema, values, onChange, emptyText = 'This agent requires no inputs.', richPost = false }) => {
  const entries = Object.entries(schema);
  if (entries.length === 0) {
    return (
      <Text style={{ color: '#6b7280', fontSize: 13 }}>
        {emptyText}
      </Text>
    );
  }
  return (
    <Form layout="vertical">
      {entries.map(([key, def]) => {
        const fieldDef = typeof def === 'object' ? def : {};
        const isPassword = fieldDef.secret || fieldDef.type === 'password';
        const isRequired = fieldDef.required !== false;
        const InputComp = isPassword ? Input.Password : Input;
        const isPostField = richPost && !isPassword && /(?:^|[_\s-])(?:post|caption|copy|text|content|context|body)(?:[_\s-]|$)/i.test(key);
        return (
          <Form.Item
            key={key}
            label={
              <span style={{ color: '#94a3b8' }}>
                {key}
                {isRequired && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                {fieldDef.description && (
                  <Text style={{ color: '#6b7280', fontSize: 11, marginLeft: 6 }}>
                    — {fieldDef.description}
                  </Text>
                )}
              </span>
            }
          >
            {isPostField ? (
              <RichPostEditor
                label={key}
                value={values[key] || ''}
                onChange={(next) => onChange({ ...values, [key]: next })}
              />
            ) : (
              <InputComp
                value={values[key] || ''}
                onChange={(e) => onChange({ ...values, [key]: e.target.value })}
                placeholder={`Enter ${key}…`}
                style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
              />
            )}
          </Form.Item>
        );
      })}
    </Form>
  );
};

const AgentDetailView: React.FC<Props> = ({ agentId, userId, onBack, onEdit, onOpenTask, projectFolders = [], onPrepareProjectFolders }) => {
  const [agent, setAgent] = useState<AgentRecord | null>(null);
  const [executions, setExecutions] = useState<AgentExecution[]>([]);
  const [schedules, setSchedules] = useState<AgentScheduleRecord[]>([]);
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AgentScheduleRecord | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [toolInputValues, setToolInputValues] = useState<Record<string, string>>({});
  const [scheduleMode, setScheduleMode] = useState<'once' | 'recurring' | 'cron'>('recurring');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTimeVal, setScheduleTimeVal] = useState('');
  const [scheduleRecurringDays, setScheduleRecurringDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [scheduleRecurringTime, setScheduleRecurringTime] = useState('09:00');
  const [cronExpr, setCronExpr] = useState('0 9 * * 1-5');
  const [scheduleTimeZone, setScheduleTimeZone] = useState(LOCAL_TIMEZONE);
  const [scheduleInputValues, setScheduleInputValues] = useState<Record<string, string>>({});
  const [scheduleToolInputValues, setScheduleToolInputValues] = useState<Record<string, string>>({});
  const [scheduleProjectId, setScheduleProjectId] = useState('');
  const scheduleProjects = projectFolders;
  const [runningScheduleId, setRunningScheduleId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'executions' | 'schedules' | 'versions'>('executions');
  const [userRating, setUserRating] = useState(0);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [agentRes, execRes, schedRes, verRes] = await Promise.all([
        api.getAgent(agentId),
        api.listAgentExecutions(agentId),
        api.listAgentSchedules(agentId),
        api.listAgentVersions(agentId),
      ]);
      setAgent(agentRes.data);
      setExecutions(execRes.data.executions || []);
      setSchedules(schedRes.data.schedules || []);
      setVersions(verRes.data.versions || []);
      api.listTools().then((toolsRes: any) => {
        const tools = Array.isArray(toolsRes.data.tools) ? toolsRes.data.tools : [];
        setAvailableTools(tools.map(normalizeToolRecord).filter(Boolean) as ToolRecord[]);
      }).catch(() => setAvailableTools([]));
      if (agentRes.data.published) {
        try {
          const ratingRes = await api.getAgentRating(agentId);
          setUserRating(ratingRes.data.rating || 0);
        } catch {
          setUserRating(0);
        }
      } else {
        setUserRating(0);
      }
    } catch {
      message.error('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  const selectedTools = useMemo(() => {
    const selected = new Set((agent?.tool_ids || []).map((id) => String(id).trim().toLowerCase()).filter(Boolean));
    return availableTools.filter((tool) => selected.has(normalizeToolId(tool).toLowerCase()));
  }, [agent?.tool_ids, availableTools]);
  const hasSocialPublishTool = (agent?.tool_ids || []).some((tool) => String(tool).toUpperCase() === 'SOCIAL_PUBLISH_POST');

  const agentInputSchema = useMemo(() => Object.keys(agent?.connector_bindings || {}).length
    ? Object.fromEntries(Object.entries(agent?.input_schema || {}).filter(([name]) =>
      !/^(connector_instance_id|.*(_api_key|_access_token|_auth_token|_password|_secret|_credentials))$/i.test(name),
    ))
    : agent?.input_schema || {}, [agent?.connector_bindings, agent?.input_schema]);

  const socialInputSchema = useMemo(
    () => optionalSocialContentSchema(agentInputSchema, hasSocialPublishTool),
    [agentInputSchema, hasSocialPublishTool],
  );

  const toolInputSchema = useMemo(
    () => buildToolInputSchema(selectedTools, agent?.input_schema || {}, Boolean(Object.keys(agent?.connector_bindings || {}).length)),
    [selectedTools, agent?.input_schema, agent?.connector_bindings],
  );

  const handleExecute = async () => {
    if (!validateRequiredInputs(socialInputSchema, inputValues, 'Agent inputs')) return;
    setExecuting(true);
    try {
      const res = await api.executeAgent(agentId, mergeNonEmptyValues(inputValues, toolInputValues));
      message.success(`Execution started (ID: ${res.data.executionId})`);
      setShowExecuteModal(false);
      setInputValues({});
      setToolInputValues({});
      void loadAll();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const resetScheduleForm = () => {
    setEditingSchedule(null);
    setScheduleProjectId('');
    setScheduleMode('recurring');
    setScheduleDate('');
    setScheduleTimeVal('');
    setScheduleRecurringDays([1, 2, 3, 4, 5]);
    setScheduleRecurringTime('09:00');
    setCronExpr('0 9 * * 1-5');
    setScheduleTimeZone(LOCAL_TIMEZONE);
    setScheduleInputValues({});
    setScheduleToolInputValues({});
  };

  const splitScheduleInputs = (inputs: Record<string, any>) => {
    const agentKeys = new Set(Object.keys(agent?.input_schema || {}).map((key) => key.toLowerCase()));
    const toolKeys = new Set(Object.keys(toolInputSchema || {}).map((key) => key.toLowerCase()));
    const agentInputs: Record<string, string> = {};
    const toolInputs: Record<string, string> = {};
    Object.entries(inputs || {}).forEach(([key, value]) => {
      if (key === '__schedule') return;
      const normalized = key.toLowerCase();
      const stringValue = String(value ?? '');
      if (agentKeys.has(normalized)) {
        agentInputs[key] = stringValue;
      } else if (toolKeys.has(normalized) || !agentKeys.has(normalized)) {
        toolInputs[key] = stringValue;
      }
    });
    return { agentInputs, toolInputs };
  };

  const openScheduleCreate = () => {
    resetScheduleForm();
    setShowScheduleModal(true);
  };

  const openScheduleEdit = (schedule: AgentScheduleRecord) => {
    const inputs = parseScheduleInputs(schedule.default_inputs_json);
    const metadata = inputs.__schedule || {};
    const mode = String(metadata.schedule_mode || '').toLowerCase();
    const { agentInputs, toolInputs } = splitScheduleInputs(inputs);

    setEditingSchedule(schedule);
    const selectedFolderId = String(metadata.project_id || '');
    setScheduleProjectId(projectFolders.some((folder) => folder.id === selectedFolderId) ? selectedFolderId : '');
    setScheduleInputValues(agentInputs);
    setScheduleToolInputValues(toolInputs);
    setScheduleTimeZone(schedule.timezone || metadata.timezone || LOCAL_TIMEZONE);
    setCronExpr(schedule.cron_expression || '0 9 * * 1-5');

    if (mode === 'once') {
      setScheduleMode('once');
      setScheduleDate(String(metadata.scheduled_date || ''));
      setScheduleTimeVal(String(metadata.scheduled_time || ''));
    } else if (mode === 'recurring') {
      setScheduleMode('recurring');
      setScheduleRecurringDays(
        Array.isArray(metadata.recurring_days) && metadata.recurring_days.length
          ? metadata.recurring_days.map((day: any) => Number(day)).filter((day: number) => Number.isInteger(day) && day >= 0 && day <= 6)
          : [1, 2, 3, 4, 5],
      );
      setScheduleRecurringTime(String(metadata.recurring_time || '09:00'));
      setScheduleDate('');
      setScheduleTimeVal('');
    } else {
      setScheduleMode('cron');
      setScheduleDate('');
      setScheduleTimeVal('');
      setScheduleRecurringDays([1, 2, 3, 4, 5]);
      setScheduleRecurringTime('09:00');
    }
    setShowScheduleModal(true);
  };

  const toggleRecurringDay = (day: number) => {
    setScheduleRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const buildSchedulePayload = (): { cron: string; metadata: Record<string, any>; timezone: string } | null => {
    if (scheduleMode === 'cron') {
      const cron = cronExpr.trim();
      if (!cron) {
        message.error('Cron expression is required');
        return null;
      }
      return { cron, timezone: scheduleTimeZone, metadata: { schedule_mode: 'cron', timezone: scheduleTimeZone } };
    }

    if (scheduleMode === 'once') {
      if (!scheduleDate || !scheduleTimeVal) {
        message.warning('Enter a date and time');
        return null;
      }
      const scheduledDate = new Date(`${scheduleDate}T${scheduleTimeVal}`);
      if (Number.isNaN(scheduledDate.getTime())) {
        message.warning('Please select a valid date and time');
        return null;
      }
      const [hour, minute] = scheduleTimeVal.split(':').map((v) => Number(v));
      const cron = `${minute} ${hour} ${scheduledDate.getDate()} ${scheduledDate.getMonth() + 1} *`;
      return {
        cron,
        timezone: scheduleTimeZone,
        metadata: {
          schedule_mode: 'once',
          scheduled_at: scheduledDate.toISOString(),
          scheduled_date: scheduleDate,
          scheduled_time: scheduleTimeVal,
          timezone: scheduleTimeZone,
        },
      };
    }

    if (scheduleRecurringDays.length === 0) {
      message.warning('Select at least one day');
      return null;
    }
    if (!scheduleRecurringTime) {
      message.warning('Enter a time');
      return null;
    }
    const [hour, minute] = scheduleRecurringTime.split(':').map((v) => Number(v));
    const days = [...scheduleRecurringDays].sort((a, b) => a - b).join(',');
    return {
      cron: `${minute} ${hour} * * ${days}`,
      timezone: scheduleTimeZone,
      metadata: {
        schedule_mode: 'recurring',
        recurring_days: [...scheduleRecurringDays].sort((a, b) => a - b),
        recurring_time: scheduleRecurringTime,
        timezone: scheduleTimeZone,
      },
    };
  };

  const handleScheduleSubmit = async () => {
    const schedulePayload = buildSchedulePayload();
    if (!schedulePayload) return;
    if (!validateRequiredInputs(socialInputSchema, scheduleInputValues, 'Agent inputs')) return;
    const defaultInputs = {
      ...mergeNonEmptyValues(scheduleInputValues, scheduleToolInputValues),
      __schedule: {
        ...schedulePayload.metadata,
        project_id: scheduleProjectId,
        project_folder: Boolean(scheduleProjectId),
        project_name: scheduleProjects.find((project) => project.id === scheduleProjectId)?.name
          || parseScheduleInputs(editingSchedule?.default_inputs_json || '{}').__schedule?.project_name || '',
      },
    };
    try {
      if (scheduleProjectId) await onPrepareProjectFolders?.();
      if (editingSchedule) {
        await api.updateAgentSchedule(agentId, editingSchedule.id, {
          cron_expression: schedulePayload.cron,
          default_inputs: defaultInputs,
          timezone: schedulePayload.timezone,
        });
        message.success('Schedule updated');
      } else {
        await api.createAgentSchedule(agentId, schedulePayload.cron, defaultInputs, true, schedulePayload.timezone);
        message.success('Schedule created');
      }
      setShowScheduleModal(false);
      resetScheduleForm();
      void loadAll();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || `Failed to ${editingSchedule ? 'update' : 'create'} schedule`);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    try {
      await api.deleteAgentSchedule(agentId, scheduleId);
      message.success('Schedule deleted');
      void loadAll();
    } catch {
      message.error('Failed to delete schedule');
    }
  };

  const handleToggleSchedule = async (schedule: AgentScheduleRecord) => {
    try {
      await api.updateAgentSchedule(agentId, schedule.id, { enabled: !schedule.enabled });
      void loadAll();
    } catch {
      message.error('Failed to update schedule');
    }
  };

  const handleRunScheduleNow = async (schedule: AgentScheduleRecord) => {
    setRunningScheduleId(schedule.id);
    try {
      const inputs = parseScheduleInputs(schedule.default_inputs_json);
      const runnableInputs = { ...inputs };
      const res = await api.executeAgent(agentId, runnableInputs, 'SCHEDULE');
      message.success(`Scheduled input set started (ID: ${res.data.executionId})`);
      if (res.data.taskId && onOpenTask) {
        onOpenTask(res.data.taskId);
      }
      void loadAll();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to run schedule now');
    } finally {
      setRunningScheduleId(null);
    }
  };

  const handlePublish = async () => {
    if (!agent) return;
    try {
      await api.publishAgent(agent.id, 'PUBLIC');
      message.success('Agent published to Marketplace');
      void loadAll();
    } catch {
      message.error('Failed to publish');
    }
  };

  const handleUnpublish = async () => {
    if (!agent) return;
    try {
      await api.unpublishAgent(agent.id);
      message.success('Agent unpublished');
      void loadAll();
    } catch {
      message.error('Failed to unpublish');
    }
  };

  const handleRateAgent = async (value: number) => {
    if (!agent || !agent.published) return;
    try {
      const res = await api.rateAgent(agent.id, value);
      setUserRating(value);
      setAgent({
        ...agent,
        rating_average: res.data.rating_average,
        rating_count: res.data.rating_count,
      });
      message.success('Rating saved');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to save rating');
    }
  };

  const externalOrigin = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'https://praxys.ai';
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div style={{ padding: 24 }}>
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
            Back
          </Button>
        </div>
        <Empty description="Agent not found" style={{ marginTop: 40 }} />
      </div>
    );
  }

  const isOwner = agent.author_id === userId;
  const kbCount = (agent.knowledge_base_ids || []).length;
  const toolCount = (agent.tool_ids || []).length;
  const inputEntries = Object.entries(agentInputSchema);
  const outputEntries = Object.entries(agent.output_schema || {});
  const apiEndpoint = `${externalOrigin}/api/agents/${agent.id}/execute`;
  const apiSampleBody = buildApiSampleBody(agentInputSchema);

  const executionColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) => {
        const status = normalizeStatus(s);
        return (
          <Tag icon={STATUS_ICON[status]} color={STATUS_COLOR[status] || 'default'}>
            {status}
          </Tag>
        );
      },
    },
    { title: 'Source', dataIndex: 'execution_source' },
    { title: 'Requested By', dataIndex: 'requested_by', ellipsis: true },
    {
      title: 'Started',
      dataIndex: 'started_at',
      render: (d: string) => d ? new Date(d).toLocaleString() : '—',
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      render: (d: string) => d ? new Date(d).toLocaleString() : '—',
    },
  ];

  return (
    <div className="praxys-agent-detail-page" style={{ padding: '20px 24px', height: '100%', overflowY: 'auto' }}>
      {/* Back + Header */}
      <div style={{ marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} className="praxys-agent-pill-btn praxys-agent-pill-btn--accent" >
          Back to Agents
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div className="praxys-agent-detail-icon">{renderAgentIcon(agent.icon, 40)}</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Title level={3} style={{ margin: 0, color: '#f1f5f9' }}>{agent.name}</Title>
                {agent.featured && <StarFilled style={{ color: '#f59e0b' }} />}
              </div>
              <Space size={4}>
                <Tag color={agent.published ? 'green' : 'default'}>
                  {agent.published ? 'Published' : 'Draft'}
                </Tag>
                <Tag>
                  {agent.visibility === 'PRIVATE' && <LockOutlined />}
                  {agent.visibility === 'ORGANIZATION' && <TeamOutlined />}
                  {agent.visibility === 'PUBLIC' && <GlobalOutlined />}
                  {' '}{agent.visibility}
                </Tag>
                <Tag>v{agent.version}</Tag>
                <Tag color="blue">{agent.category}</Tag>
                {kbCount > 0 && (
                  <Tag color="geekblue"><BookOutlined /> {kbCount} KB{kbCount !== 1 ? 's' : ''}</Tag>
                )}
                {toolCount > 0 && (
                  <Tag color="purple"><ToolOutlined /> {toolCount} tool{toolCount !== 1 ? 's' : ''}</Tag>
                )}
              </Space>
              {agent.published && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <Rate
                    allowClear={false}
                    value={userRating}
                    onChange={handleRateAgent}
                    style={{ color: '#38bdf8', fontSize: 16 }}
                  />
                  <Text style={{ color: '#94a3b8', fontSize: 12 }}>
                    {(agent.rating_average || 0).toFixed(1)} ({agent.rating_count || 0})
                  </Text>
                </div>
              )}
            </div>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              style={{ background: '#10b981', borderColor: '#10b981' }}
              onClick={() => setShowExecuteModal(true)}
            >
              Run Now
            </Button>
            {isOwner && (
              <>
                <Button
                  className="praxys-agent-pill-btn praxys-agent-pill-btn--accent"
                  icon={<CalendarOutlined />}
                  onClick={() => setShowScheduleModal(true)}
                >
                  Schedule
                </Button>
                <Button className="praxys-agent-pill-btn praxys-agent-pill-btn--accent" icon={<EditOutlined />} onClick={() => onEdit(agent)}>
                  Edit
                </Button>
                {agent.published ? (
                  <Button className="praxys-agent-pill-btn praxys-agent-pill-btn--accent" onClick={handleUnpublish}>Unpublish</Button>
                ) : (
                  <Button
                    icon={<GlobalOutlined />}
                    style={{ color: '#6366f1', borderColor: '#6366f1' }}
                    onClick={handlePublish}
                  >
                    Publish
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>
      </div>

      {/* Description + Stats */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={16}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Paragraph style={{ color: '#94a3b8', marginBottom: 0 }}>
              {agent.description || agent.short_description || 'No description provided.'}
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: '#6b7280' }}
              contentStyle={{ color: '#e2e8f0' }}
            >
              <Descriptions.Item label="Knowledge Bases">
                {kbCount > 0
                  ? <Tag color="geekblue"><BookOutlined /> {kbCount} attached</Tag>
                  : valueText('None')}
              </Descriptions.Item>
              <Descriptions.Item label="Tools">
                {toolCount > 0
                  ? <Tag color="purple"><ToolOutlined /> {toolCount} selected</Tag>
                  : valueText('None')}
              </Descriptions.Item>
              <Descriptions.Item label="Inputs">
                {valueText(inputEntries.length > 0 ? inputEntries.length : 'None')}
              </Descriptions.Item>
              <Descriptions.Item label="Outputs">
                {valueText(outputEntries.length > 0 ? outputEntries.length : 'None')}
              </Descriptions.Item>
              <Descriptions.Item label="Executions">
                {valueText(agent.execution_count.toLocaleString())}
              </Descriptions.Item>
              <Descriptions.Item label="Subscribers">
                {valueText(agent.subscriber_count.toLocaleString())}
              </Descriptions.Item>
              <Descriptions.Item label="API Access">
                {agent.allow_api_execution
                  ? <Tag color="blue"><ApiOutlined /> Enabled</Tag>
                  : <Tag>Disabled</Tag>}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* System Prompt */}
      {agent.system_prompt && (
        <Collapse
          ghost
          style={{ marginBottom: 16, background: '#0f172a', borderRadius: 10, border: '1px solid #1e293b' }}
        >
          <Collapse.Panel
            key="prompt"
            header={
              <Text strong style={{ color: '#e2e8f0' }}>
                <CodeOutlined style={{ marginRight: 6, color: '#6366f1' }} />Agent Instructions
              </Text>
            }
          >
            <pre
              style={{
                color: '#94a3b8',
                fontSize: 12,
                margin: 0,
                whiteSpace: 'pre-wrap',
                maxHeight: 240,
                overflowY: 'auto',
                padding: '0 12px 12px',
              }}
            >
              {agent.system_prompt}
            </pre>
          </Collapse.Panel>
        </Collapse>
      )}

      {/* Tools */}
      {toolCount > 0 && (
        <Card
          size="small"
          style={{ borderRadius: 10, marginBottom: 16 }}
          title={
            <Text strong style={{ color: '#e2e8f0' }}>
              <ToolOutlined style={{ marginRight: 6, color: '#6366f1' }} />Tools
            </Text>
          }
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(agent.tool_ids || []).map((id) => (
              <Tag key={id} color="purple"><ToolOutlined style={{ marginRight: 4 }} />{id}</Tag>
            ))}
          </div>
        </Card>
      )}

      {/* API Snippet */}
      {agent.allow_api_execution && (
        <Card
          size="small"
          style={{ borderRadius: 10, background: '#0f172a', border: '1px solid #1e293b', marginBottom: 20 }}
        >
          <Text strong style={{ color: '#e2e8f0' }}>
            <ApiOutlined style={{ marginRight: 6 }} />API Endpoint
          </Text>
          <pre style={{ color: '#818cf8', fontSize: 12, margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
            {`POST ${apiEndpoint}\n${JSON.stringify(apiSampleBody, null, 2)}`}
          </pre>
        </Card>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['executions', 'schedules', 'versions'] as const).map((tab) => (
          <Button
            key={tab}
            className="praxys-agent-tab-btn"
            type={activeTab === tab ? 'primary' : 'default'}
            size="small"
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
        <Button
          className="praxys-agent-pill-btn"
          icon={<ReloadOutlined />}
          size="small"
          onClick={loadAll}
          style={{ marginLeft: 'auto' }}
        />
      </div>

      {activeTab === 'executions' && (
        executions.length === 0
          ? <Empty description="No executions yet" />
          : <Table
              className="praxys-dark-table"
              dataSource={executions}
              columns={executionColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
            />
      )}

      {activeTab === 'schedules' && (
        <div>
          {isOwner && (
            <Button
              className="praxys-agent-pill-btn praxys-agent-pill-btn--accent"
              icon={<PlusOutlined />}
              size="small"
              style={{ marginBottom: 12 }}
              onClick={openScheduleCreate}
            >
              Add Schedule
            </Button>
          )}
          {schedules.length === 0
            ? <Empty description="No schedules configured" />
            : (
              <Table
                className="praxys-dark-table"
                dataSource={schedules}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: 'Cron Expression', dataIndex: 'cron_expression', render: (v: string) => <code>{v}</code> },
                  {
                    title: 'Project folder',
                    dataIndex: 'default_inputs_json',
                    render: (raw: string) => {
                      const metadata = parseScheduleInputs(raw).__schedule || {};
                      return metadata.project_id ? metadata.project_name || 'Assigned project' : 'No project';
                    },
                  },
                  {
                    title: 'Input Set',
                    dataIndex: 'default_inputs_json',
                    render: (raw: string, row: AgentScheduleRecord) => {
                      const inputs = parseScheduleInputs(raw);
                      const visibleKeys = Object.keys(inputs).filter((key) => key !== '__schedule');
                      const mode = inputs.__schedule?.schedule_mode;
                      const timezone = row.timezone || inputs.__schedule?.timezone || 'UTC';
                      return (
                        <Space size={4} wrap>
                          {mode && <Tag>{String(mode).toUpperCase()}</Tag>}
                          <Tag color="cyan">{timezone}</Tag>
                          <Tag color={visibleKeys.length ? 'geekblue' : 'default'}>
                            {visibleKeys.length} input{visibleKeys.length !== 1 ? 's' : ''}
                          </Tag>
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Enabled',
                    dataIndex: 'enabled',
                    render: (v: boolean, row: AgentScheduleRecord) => (
                      <Switch
                        checked={v}
                        onChange={() => handleToggleSchedule(row)}
                        disabled={!isOwner}
                        size="small"
                      />
                    ),
                  },
                  {
                    title: 'Last Run',
                    dataIndex: 'last_run_at',
                    render: (d: string) => (d ? new Date(d).toLocaleString() : 'Never'),
                  },
                  { title: 'Created', dataIndex: 'created_at', render: (d: string) => new Date(d).toLocaleDateString() },
                  ...(isOwner ? [{
                    title: '',
                    render: (_: any, row: AgentScheduleRecord) => (
                      <Space size={6}>
                        <Button
                          className="praxys-agent-pill-btn praxys-agent-pill-btn--accent"
                          size="small"
                          icon={<PlayCircleOutlined />}
                          loading={runningScheduleId === row.id}
                          disabled={Boolean(runningScheduleId)}
                          onClick={() => handleRunScheduleNow(row)}
                        >
                          Run
                        </Button>
                        <Button
                          className="praxys-agent-pill-btn"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openScheduleEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            Modal.confirm({
                              title: 'Delete schedule?',
                              onOk: () => handleDeleteSchedule(row.id),
                            })
                          }
                        />
                      </Space>
                    ),
                  }] : []),
                ]}
              />
            )
          }
        </div>
      )}

      {activeTab === 'versions' && (
        versions.length === 0
          ? <Empty description="No published versions yet" />
          : (
            <Table
              className="praxys-dark-table"
              dataSource={versions}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: 'Version', dataIndex: 'version', render: (v: string) => <Tag>v{v}</Tag> },
                { title: 'Change Log', dataIndex: 'change_log', ellipsis: true },
                {
                  title: 'Published',
                  dataIndex: 'published_at',
                  render: (d: string) => new Date(d).toLocaleDateString(),
                },
              ]}
            />
          )
      )}

      {/* Execute Modal — dynamic form */}
      <Modal
        open={showExecuteModal}
        title={<span><RocketOutlined style={{ marginRight: 6 }} /> Run Agent</span>}
        onCancel={() => { setShowExecuteModal(false); setInputValues({}); setToolInputValues({}); }}
        onOk={handleExecute}
        okText="Execute"
        okButtonProps={{ loading: executing, style: { background: '#10b981', borderColor: '#10b981' } }}
        width={640}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text style={{ color: '#d1d5db', display: 'block', marginBottom: 8 }}>
              Agent inputs
            </Text>
            <DynamicInputForm
              schema={socialInputSchema}
              values={inputValues}
              onChange={setInputValues}
              richPost={hasSocialPublishTool}
            />
          </div>
          {Object.keys(toolInputSchema).length > 0 && (
            <div>
              <Text style={{ color: '#d1d5db', display: 'block', marginBottom: 4 }}>
                Tool inputs
              </Text>
              <Text style={{ color: '#6b7280', display: 'block', fontSize: 12, marginBottom: 8 }}>
                Values can reference agent inputs with templates, for example <code>{'{{email}}'}</code>.
              </Text>
              <DynamicInputForm
                schema={toolInputSchema}
                values={toolInputValues}
                onChange={setToolInputValues}
                richPost={hasSocialPublishTool}
                emptyText="Selected tools require no extra inputs."
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Schedule Modal */}
      <Modal
        open={showScheduleModal}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined style={{ color: '#10a37f' }} />
            {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
          </span>
        }
        onCancel={() => {
          setShowScheduleModal(false);
          resetScheduleForm();
        }}
        onOk={handleScheduleSubmit}
        okText={editingSchedule ? 'Update Schedule' : 'Create Schedule'}
        width={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
          <div>
            <label htmlFor="schedule-project" style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 8 }}>
              Project folder
            </label>
            <Select
              id="schedule-project"
              aria-label="Project folder for scheduled runs"
              value={scheduleProjectId || undefined}
              onChange={(value) => setScheduleProjectId(value || '')}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select a project folder"
              notFoundContent="No project folders yet. Create one in the sidebar."
              style={{ width: '100%' }}
              options={scheduleProjects.map((project) => ({ value: project.id, label: project.name }))}
            />
            <Text style={{ color: '#6b7280', display: 'block', fontSize: 12, marginTop: 4 }}>
              Choose a folder from Projects in the sidebar. Each run will appear in that folder, including runs started with Run now.
            </Text>
          </div>
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 8 }}>
              Input set
            </label>
            <div className="praxys-agent-schedule-inputs">
              <DynamicInputForm
                schema={socialInputSchema}
                values={scheduleInputValues}
                onChange={setScheduleInputValues}
                richPost={hasSocialPublishTool}
              />
            </div>
          </div>

          {Object.keys(toolInputSchema).length > 0 && (
            <div>
              <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 4 }}>
                Tool inputs
              </label>
              <Text style={{ color: '#6b7280', display: 'block', fontSize: 12, marginBottom: 8 }}>
                You can use templates like <code>{'{{email}}'}</code> to map scheduled agent inputs into tool fields.
              </Text>
              <div className="praxys-agent-schedule-inputs">
                <DynamicInputForm
                  schema={toolInputSchema}
                  values={scheduleToolInputValues}
                  onChange={setScheduleToolInputValues}
                  richPost={hasSocialPublishTool}
                  emptyText="Selected tools require no extra inputs."
                />
              </div>
            </div>
          )}

          <div className="praxys-schedule-type-toggle">
            <button
              className={`praxys-schedule-type-btn${scheduleMode === 'once' ? ' active' : ''}`}
              onClick={() => setScheduleMode('once')}
              type="button"
            >
              <ClockCircleOutlined style={{ marginRight: 6 }} />One-time
            </button>
            <button
              className={`praxys-schedule-type-btn${scheduleMode === 'recurring' ? ' active' : ''}`}
              onClick={() => setScheduleMode('recurring')}
              type="button"
            >
              <CalendarOutlined style={{ marginRight: 6 }} />Recurring
            </button>
            <button
              className={`praxys-schedule-type-btn${scheduleMode === 'cron' ? ' active' : ''}`}
              onClick={() => setScheduleMode('cron')}
              type="button"
            >
              <CodeOutlined style={{ marginRight: 6 }} />Cron
            </button>
          </div>

          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Timezone
            </label>
            <Select
              value={scheduleTimeZone}
              onChange={setScheduleTimeZone}
              options={TIME_ZONE_OPTIONS}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              className="praxys-schedule-timezone-select"
            />
          </div>

          {scheduleMode === 'once' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Date</label>
                <input
                  type="date"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Time</label>
                <input
                  type="time"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleTimeVal}
                  onChange={(e) => setScheduleTimeVal(e.target.value)}
                />
              </div>
            </div>
          )}

          {scheduleMode === 'recurring' && (
            <>
              <div>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 8 }}>Days</label>
                <div className="praxys-schedule-days">
                  {DAYS_OF_WEEK.map((day, i) => (
                    <button
                      key={day}
                      type="button"
                      className={`praxys-schedule-day-btn${scheduleRecurringDays.includes(i) ? ' active' : ''}`}
                      onClick={() => toggleRecurringDay(i)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Time</label>
                <input
                  type="time"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleRecurringTime}
                  onChange={(e) => setScheduleRecurringTime(e.target.value)}
                />
              </div>
            </>
          )}

          {scheduleMode === 'cron' && (
            <div>
              <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
                Cron pattern
              </label>
              <Input
                value={cronExpr}
                onChange={(e) => setCronExpr(e.target.value)}
                placeholder="0 9 * * 1-5"
                style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
              />
              <Text style={{ fontSize: 11, color: '#6b7280' }}>
                Examples: <code>0 9 * * 1-5</code> weekdays 9am · <code>0 */4 * * *</code> every 4h
              </Text>
            </div>
          )}
        </div>
        <Form layout="vertical" style={{ display: 'none' }}>
          <Form.Item label="Cron Expression" required>
            <Input
              value={cronExpr}
              onChange={(e) => setCronExpr(e.target.value)}
              placeholder="0 9 * * 1-5"
            />
            <Text style={{ fontSize: 11, color: '#6b7280' }}>
              Examples: <code>0 9 * * 1-5</code> (weekdays 9am) · <code>0 */4 * * *</code> (every 4h)
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentDetailView;
